import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api';
import { Plus, Search, Edit2, Trash2, X, Shield, ShieldOff, ChevronDown, ChevronUp, Database, MapPin, User, ShoppingCart } from 'lucide-react';
import { useAutosave, loadDraft, clearDraft } from '../hooks/useAutosave';

const emptyCustomer = {
  name: '', contact_name: '', email: '', phone: '', company: '',
  billing_address: '', shipping_address: '', city: '', state: '', zip_code: '', country: '',
  tax_exempt: false, pricing_scheme: 'regular', payment_terms: 'Net 30', notes: ''
};

const CUST_FORM_KEY = 'webb_customer_form';

const Customers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [loading, setLoading] = useState(true);
  const custSaved = loadDraft(CUST_FORM_KEY);
  const [showForm, setShowForm] = useState(custSaved?.showForm || false);
  const [editingId, setEditingId] = useState(custSaved?.editingId || null);
  const [form, setForm] = useState(custSaved?.form || { ...emptyCustomer });
  const [saving, setSaving] = useState(false);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [expandedId, setExpandedId] = useState(null);
  const [expandedTab, setExpandedTab] = useState('info');
  const [customerOrders, setCustomerOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [priceSchemes, setPriceSchemes] = useState([]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = searchQuery ? { search: searchQuery } : {};
      const data = await api.getCustomers(params);
      setCustomers(data);
    } catch (err) {
      console.error('Failed to load customers', err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchCustomers is stable; only re-run on search change
  useEffect(() => { fetchCustomers(); }, [searchQuery]);

  // Sync search to URL
  useEffect(() => {
    if (searchQuery) setSearchParams({ search: searchQuery }, { replace: true });
    else setSearchParams({}, { replace: true });
  }, [searchQuery]); // eslint-disable-line

  useEffect(() => {
    const loadSchemes = async () => {
      try {
        const data = await api.getPriceSchemes();
        setPriceSchemes(data);
      } catch (err) { console.error('Failed to load price schemes', err); }
    };
    loadSchemes();
  }, []);

  // Autosave customer form
  useAutosave(CUST_FORM_KEY, { form, showForm, editingId });

  // Memoize sorted list so it's only recomputed when customers/sort settings change
  const sorted = useMemo(() => {
    return [...customers].sort((a, b) => {
      const aVal = a[sortField] || '';
      const bVal = b[sortField] || '';
      return sortDir === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [customers, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc'
      ? <ChevronUp className="inline h-3 w-3 ml-0.5" />
      : <ChevronDown className="inline h-3 w-3 ml-0.5" />;
  };

  const handleEdit = (customer) => {
    setEditingId(customer.id);
    setForm({
      name: customer.name || '',
      contact_name: customer.contact_name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      company: customer.company || '',
      billing_address: customer.billing_address || '',
      shipping_address: customer.shipping_address || '',
      city: customer.city || '',
      state: customer.state || '',
      zip_code: customer.zip_code || '',
      country: customer.country || '',
      tax_exempt: customer.tax_exempt || false,
      pricing_scheme: customer.pricing_scheme || 'regular',
      payment_terms: customer.payment_terms || 'Net 30',
      notes: customer.notes || '',
    });
    setShowForm(true);
  };

  const handleNew = () => {
    setEditingId(null);
    setForm({ ...emptyCustomer });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { alert('Customer name is required'); return; }
    if (!editingId) {
      // New customer validations
      if (!form.billing_address?.trim()) { alert('Billing address is required for new customers'); return; }
      if (!form.phone?.trim()) { alert('Phone number is required for new customers'); return; }
      if (!form.contact_name?.trim()) { alert('Contact person name is required for new customers'); return; }
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.updateCustomer(editingId, form);
      } else {
        // New customers require admin approval
        const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return {}; } })();
        const isAdmin = currentUser?.role === 'admin';
        await api.createCustomer({ ...form, approved: isAdmin });
      }
      setShowForm(false);
      setEditingId(null);
      clearDraft(CUST_FORM_KEY);
      fetchCustomers();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
      setExpandedTab('info');
      setCustomerOrders([]);
    }
  };

  const loadCustomerOrders = async (id) => {
    setExpandedTab('orders');
    setOrdersLoading(true);
    try {
      const data = await api.getCustomerSalesOrders(id);
      setCustomerOrders(data);
    } catch (err) {
      console.error('Failed to load orders', err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleDelete = async (e, customer) => {
    e.stopPropagation();
    if (!window.confirm(`Delete customer "${customer.name}"? This cannot be undone.`)) return;
    try {
      await api.deleteCustomer(customer.id);
      fetchCustomers();
      if (expandedId === customer.id) setExpandedId(null);
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '';
  const fmtCurrency = (v) => `$${(v || 0).toFixed(2)}`;

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Customers</h2>
          <p className="text-muted-foreground">{sorted.length} customers</p>
        </div>
        <button onClick={handleNew} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90" data-testid="new-customer-btn">
          <Plus className="h-4 w-4" /> New Customer
        </button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text" placeholder="Search customers..."
          value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-4 py-2 border border-input rounded-md"
          data-testid="customer-search"
        />
      </div>

      {/* Customer Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="customer-form-modal">
          <div className="bg-card rounded-lg border border-border p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{editingId ? 'Edit Customer' : 'New Customer'}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-muted rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Customer Name *</label>
                <input type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="form-name" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Contact Name {!editingId && <span className="text-red-500">*</span>}</label>
                <input type="text" value={form.contact_name} onChange={(e) => setForm({...form, contact_name: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="form-contact" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Company</label>
                <input type="text" value={form.company} onChange={(e) => setForm({...form, company: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="form-company" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="form-email" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone {!editingId && <span className="text-red-500">*</span>}</label>
                <input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="form-phone" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Billing Address {!editingId && <span className="text-red-500">*</span>}</label>
                <input type="text" value={form.billing_address} onChange={(e) => setForm({...form, billing_address: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="form-billing-address" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">City</label>
                <input type="text" value={form.city} onChange={(e) => setForm({...form, city: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="form-city" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">State</label>
                <input type="text" value={form.state} onChange={(e) => setForm({...form, state: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="form-state" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">ZIP Code</label>
                <input type="text" value={form.zip_code} onChange={(e) => setForm({...form, zip_code: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="form-zip" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Country</label>
                <input type="text" value={form.country} onChange={(e) => setForm({...form, country: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="form-country" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Shipping Address</label>
                <input type="text" value={form.shipping_address} onChange={(e) => setForm({...form, shipping_address: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="form-shipping-address" />
              </div>
              <div className="col-span-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className={`relative w-11 h-6 rounded-full transition-colors ${form.tax_exempt ? 'bg-green-500' : 'bg-gray-300'}`}
                    onClick={() => setForm({...form, tax_exempt: !form.tax_exempt})} data-testid="form-tax-exempt">
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.tax_exempt ? 'translate-x-5' : ''}`} />
                  </div>
                  <span className="text-sm font-medium">
                    {form.tax_exempt ? (
                      <span className="flex items-center gap-1 text-green-700"><Shield className="h-4 w-4" /> Tax Exempt</span>
                    ) : (
                      <span className="flex items-center gap-1 text-muted-foreground"><ShieldOff className="h-4 w-4" /> Not Tax Exempt</span>
                    )}
                  </span>
                </label>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Pricing Scheme</label>
                <select value={form.pricing_scheme} onChange={(e) => setForm({...form, pricing_scheme: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="form-pricing-scheme">
                  <option value="regular">Regular Price (20% markup)</option>
                  <option value="pwo">PW OIL Price (at cost)</option>
                  {priceSchemes.filter(s => s.name !== 'regular' && s.name !== 'pwo').map(s => (
                    <option key={s.name} value={s.name}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Payment Terms</label>
                <select value={form.payment_terms} onChange={(e) => setForm({...form, payment_terms: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="form-payment-terms">
                  <option value="Due Upon Receipt">Due Upon Receipt</option>
                  <option value="Net 15">Net 15</option>
                  <option value="Net 30">Net 30</option>
                  <option value="Net 45">Net 45</option>
                  <option value="Net 60">Net 60</option>
                  <option value="Net 90">Net 90</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md h-20" data-testid="form-notes" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-input rounded-md hover:bg-muted" data-testid="cancel-customer-btn">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50" data-testid="save-customer-btn">
                {saving ? 'Saving...' : (editingId ? 'Update Customer' : 'Create Customer')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="w-8 px-2"></th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('name')}>
                  Name <SortIcon field="name" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Phone</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Email</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">City / State</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Tax Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Pricing</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr><td colSpan="10" className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan="10" className="px-4 py-8 text-center text-muted-foreground">No customers found</td></tr>
              ) : (
                sorted.map((c) => (
                  <React.Fragment key={c.id}>
                    <tr className="hover:bg-muted/30 cursor-pointer" data-testid={`customer-row-${c.id}`} onClick={() => toggleExpand(c.id)}>
                      <td className="px-2 py-3 text-center">
                        {expandedId === c.id
                          ? <ChevronUp className="h-4 w-4 text-muted-foreground inline" />
                          : <ChevronDown className="h-4 w-4 text-muted-foreground inline" />}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium">{c.name}</p>
                        {c.company && <p className="text-xs text-muted-foreground">{c.company}</p>}
                      </td>
                      <td className="px-4 py-3 text-sm">{c.contact_name || '\u2014'}</td>
                      <td className="px-4 py-3 text-sm">{c.phone || '\u2014'}</td>
                      <td className="px-4 py-3 text-sm">{c.email || '\u2014'}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {[c.city, c.state].filter(Boolean).join(', ') || '\u2014'}
                      </td>
                      <td className="px-4 py-3">
                        {c.tax_exempt ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700"><Shield className="h-3 w-3" /> Exempt</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Taxable</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          c.pricing_scheme === 'pwo' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {c.pricing_scheme === 'pwo' ? 'PW OIL' : 'Regular'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {c.credit_denied ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700" data-testid={`credit-denied-badge-${c.id}`}>Credit Denied</span>
                        ) : c.approved === false ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700" data-testid={`pending-badge-${c.id}`}>Pending</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">Approved</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {(c.approved === false || c.credit_denied) && (
                            <button onClick={async (e) => {
                              e.stopPropagation();
                              try { await api.request(`/api/customers/${c.id}/approve`, { method: 'POST' }); fetchCustomers(); }
                              catch (err) { alert(err.message); }
                            }} className="p-1.5 hover:bg-green-50 rounded-md" title="Approve Customer" data-testid={`approve-customer-${c.id}`}>
                              <Shield className="h-4 w-4 text-green-600" />
                            </button>
                          )}
                          {c.approved !== false && !c.credit_denied && (
                            <button onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm(`Deny credit to "${c.name}"? This will prevent any sales orders for this customer.`)) return;
                              try { await api.request(`/api/customers/${c.id}/unapprove`, { method: 'POST' }); fetchCustomers(); }
                              catch (err) { alert(err.message); }
                            }} className="p-1.5 hover:bg-red-50 rounded-md" title="Deny Credit / Unapprove" data-testid={`unapprove-customer-${c.id}`}>
                              <ShieldOff className="h-4 w-4 text-red-500" />
                            </button>
                          )}
                          <button onClick={(e) => { e.stopPropagation(); handleEdit(c); }} className="p-1.5 hover:bg-muted rounded-md" data-testid={`edit-customer-${c.id}`}>
                            <Edit2 className="h-4 w-4 text-muted-foreground" />
                          </button>
                          <button onClick={(e) => handleDelete(e, c)} className="p-1.5 hover:bg-red-50 rounded-md" data-testid={`delete-customer-${c.id}`}>
                            <Trash2 className="h-4 w-4 text-red-400 hover:text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedId === c.id && (
                      <tr data-testid={`customer-detail-${c.id}`}>
                        <td colSpan="9" className="px-6 py-4 bg-muted/20">
                          {/* Tabs */}
                          <div className="flex gap-4 mb-4 border-b border-border">
                            <button onClick={() => setExpandedTab('info')}
                              className={`pb-2 text-sm font-medium border-b-2 transition-colors ${expandedTab === 'info' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                              data-testid="tab-info">
                              <User className="inline h-3.5 w-3.5 mr-1.5" />Customer Info
                            </button>
                            <button onClick={() => loadCustomerOrders(c.id)}
                              className={`pb-2 text-sm font-medium border-b-2 transition-colors ${expandedTab === 'orders' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                              data-testid="tab-orders">
                              <ShoppingCart className="inline h-3.5 w-3.5 mr-1.5" />Past Sales Orders
                            </button>
                          </div>

                          {expandedTab === 'info' && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                              <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-3 flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> Contact Info</h4>
                              <div className="space-y-2 text-sm">
                                {c.contact_name && <div><span className="text-muted-foreground">Contact:</span> {c.contact_name}</div>}
                                {c.phone && <div><span className="text-muted-foreground">Phone:</span> {c.phone}</div>}
                                {c.email && <div><span className="text-muted-foreground">Email:</span> {c.email}</div>}
                                {c.company && <div><span className="text-muted-foreground">Company:</span> {c.company}</div>}
                              </div>
                            </div>
                            <div>
                              <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-3 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Addresses</h4>
                              <div className="space-y-2 text-sm">
                                {c.billing_address && <div><span className="text-muted-foreground">Billing:</span> {c.billing_address}</div>}
                                {c.shipping_address && <div><span className="text-muted-foreground">Shipping:</span> {c.shipping_address}</div>}
                                <div><span className="text-muted-foreground">Location:</span> {[c.city, c.state, c.zip_code, c.country].filter(Boolean).join(', ') || '\u2014'}</div>
                              </div>
                            </div>
                            <div>
                              <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-3 flex items-center gap-1.5"><Database className="h-3.5 w-3.5" /> Details</h4>
                              <div className="space-y-2 text-sm">
                                <div><span className="text-muted-foreground">Tax Status:</span> {c.tax_exempt ? 'Tax Exempt' : 'Taxable'}</div>
                                <div><span className="text-muted-foreground">Pricing:</span> {c.pricing_scheme === 'pwo' ? 'PW OIL Price (at cost)' : 'Regular Price (20% markup)'}</div>
                                <div><span className="text-muted-foreground">Terms:</span> {c.payment_terms || 'Net 30'}</div>
                                {c.notes && <div><span className="text-muted-foreground">Notes:</span> {c.notes}</div>}
                              </div>
                            </div>
                            {/* Raw Import Data */}
                            {c.raw_data && Object.keys(c.raw_data).length > 0 && (
                              <div className="md:col-span-3 mt-2 pt-3 border-t border-border">
                                <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">All Import Data</p>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
                                  {Object.entries(c.raw_data).filter(([, v]) => v && String(v).trim()).map(([k, v]) => (
                                    <div key={k} className="bg-muted/40 rounded px-2 py-1">
                                      <span className="text-[10px] text-muted-foreground font-mono block">{k}</span>
                                      <span className="text-xs">{String(v)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          )}

                          {expandedTab === 'orders' && (
                          <div>
                            {ordersLoading ? (
                              <p className="text-sm text-muted-foreground py-4 text-center">Loading orders...</p>
                            ) : customerOrders.length === 0 ? (
                              <p className="text-sm text-muted-foreground py-4 text-center">No sales orders found for this customer</p>
                            ) : (
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b">
                                    <th className="text-left py-2 text-xs font-medium text-muted-foreground uppercase">Order #</th>
                                    <th className="text-left py-2 text-xs font-medium text-muted-foreground uppercase">Date</th>
                                    <th className="text-left py-2 text-xs font-medium text-muted-foreground uppercase">Picked Up By</th>
                                    <th className="text-left py-2 text-xs font-medium text-muted-foreground uppercase">Lease</th>
                                    <th className="text-left py-2 text-xs font-medium text-muted-foreground uppercase">Status</th>
                                    <th className="text-left py-2 text-xs font-medium text-muted-foreground uppercase">Payment</th>
                                    <th className="text-right py-2 text-xs font-medium text-muted-foreground uppercase">Total</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                  {customerOrders.map((o) => (
                                    <tr key={o.id} className="hover:bg-muted/30">
                                      <td className="py-2">
                                        <Link to={`/sales-orders/${o.id}`} className="text-primary hover:underline font-medium" data-testid={`order-link-${o.id}`}>
                                          {o.order_number}
                                        </Link>
                                      </td>
                                      <td className="py-2 text-muted-foreground">{fmtDate(o.order_date || o.created_at)}</td>
                                      <td className="py-2">{o.picked_up_by || '\u2014'}</td>
                                      <td className="py-2">{o.lease_name || '\u2014'}</td>
                                      <td className="py-2">
                                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                          o.status === 'completed' ? 'bg-green-100 text-green-700' :
                                          o.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                                          o.status === 'draft' ? 'bg-amber-100 text-amber-700' :
                                          'bg-gray-100 text-gray-600'
                                        }`}>{o.status}</span>
                                      </td>
                                      <td className="py-2">
                                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                          o.payment_status === 'completed' ? 'bg-green-100 text-green-700' :
                                          'bg-red-100 text-red-600'
                                        }`}>{o.payment_status === 'completed' ? 'Paid' : 'Unpaid'}</span>
                                      </td>
                                      <td className="py-2 text-right tabular-nums font-medium">{fmtCurrency(o.total)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Customers;
